<template>
  <div class="w-full">
    <Head :title="category.name">
      <meta
        head-key="description"
        name="description"
        :content="
          category.subtitle || `Utforska recept i kategorin ${category.name}.`
        "
      />
    </Head>
    <!-- Breadcrumb -->
    <div
      class="max-w-[1280px] mx-auto px-4 md:px-6 pt-6 pb-0 flex items-center gap-2"
    >
      <Link
        :href="categoriesPath"
        class="font-[Poppins] font-bold text-sm text-teal-500 tracking-[1.4px] uppercase no-underline"
        >Kategorier</Link
      >
      <template v-if="category.parent">
        <span class="font-[Poppins] font-bold text-sm text-teal-500">/</span>
        <Link
          :href="category.parent.path"
          class="font-[Poppins] font-bold text-sm text-teal-500 tracking-[1.4px] uppercase no-underline"
          >{{ category.parent.name }}</Link
        >
      </template>
      <span class="font-[Poppins] font-bold text-sm text-teal-500">/</span>
      <span
        class="font-[Poppins] text-sm text-[#191c1e] tracking-[1.4px] uppercase"
        >{{ category.name }}</span
      >
    </div>

    <section class="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-16">
      <div
        class="relative overflow-hidden rounded-[24px] md:rounded-[32px]"
        style="height: clamp(240px, 40vw, 420px)"
      >
        <img
          v-if="category.cover_image_url"
          :src="category.cover_image_url"
          :alt="category.name"
          class="w-full h-full object-cover"
        />
        <div
          v-else
          class="w-full h-full bg-gradient-to-br from-teal-400 to-teal-600"
        />
        <div
          class="absolute inset-0 flex flex-col justify-center p-6 md:p-12"
          style="
            background: linear-gradient(
              to right,
              rgba(0, 0, 0, 0.7) 0%,
              rgba(0, 0, 0, 0.2) 60%,
              rgba(0, 0, 0, 0) 100%
            );
          "
        >
          <h1
            class="font-[Poppins] font-extrabold text-white tracking-[-2px] leading-[1.1] m-0 mb-3"
            style="font-size: clamp(28px, 5vw, 52px)"
          >
            {{ category.name }}
          </h1>
          <p
            class="hidden sm:block text-[16px] text-white/90 max-w-[400px] mb-4"
          >
            {{
              category.subtitle ||
              `Alla recept inom ${category.name.toLowerCase()} samlade på ett ställe.`
            }}
          </p>
          <div v-if="category.children?.length" class="flex flex-wrap gap-2">
            <Link
              v-for="child in category.children"
              :key="child.id"
              :href="child.path"
              class="px-4 py-2 rounded-full bg-white/20 text-white text-sm font-semibold hover:bg-white/30 transition-colors backdrop-blur-sm"
            >
              {{ child.name }}
            </Link>
          </div>
          <div v-if="currentUser && currentUser.admin" class="mt-4">
            <Link
              :href="editCategoryPath"
              class="text-[13px] font-semibold text-white border border-white/40 bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-lg transition-colors backdrop-blur-sm"
            >
              Edit category
            </Link>
          </div>
        </div>
      </div>
    </section>

    <section
      id="recipes"
      class="max-w-[1280px] mx-auto px-4 md:px-6 pb-12 md:pb-16"
    >
      <h2
        class="font-[Poppins] font-bold text-[#191c1e] text-[clamp(24px,3vw,34px)] mb-6"
      >
        Recept i {{ category.name }}
      </h2>

      <div
        v-if="recipes.length > 0"
        class="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6"
      >
        <RecipeCard
          v-for="recipe in recipes"
          :key="recipe.id"
          :recipe="recipe"
          :current-user="currentUser"
          :csrf-token="csrfToken"
        />
      </div>
      <div v-else class="text-lg text-[#6c7a77]">Hittade inga recept</div>
    </section>
  </div>
</template>

<script setup>
import { Head, Link } from "@inertiajs/vue3";
import RecipeCard from "@/components/recipe/RecipeCard.vue";

defineProps({
  category: { type: Object, required: true },
  recipes: { type: Array, default: () => [] },
  currentUser: { type: Object, default: null },
  csrfToken: { type: String, default: "" },
  editCategoryPath: { type: String, default: "" },
  categoriesPath: { type: String, default: "" },
});
</script>
